import React, { useEffect, useState } from 'react';
import { LayoutTemplate, Loader2, Plus, Sparkles, Trash2 } from 'lucide-react';
import type { AccountPlan } from '../../global';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input, Label, Separator } from '../../components/ui/Input';
import { useLanguage } from '../../lib/i18n';
import { usePreferences } from './usePreferences';
import { TemplatePickerDialog } from './TemplatePickerDialog';
import { recommendedRules, templateById } from './summaryTemplates';

interface SummaryStyleSectionProps {
  /** Kilitli şablonları göstermek için hesabın planı gerekiyor. */
  onNotify: (kind: ToastKind, text: string) => void;
}

/** Kural sayısı sınırı — ana süreçteki sanitize ile aynı (settings.ts). */
const MAX_RULES = 10;
const MAX_RULE_LENGTH = 200;

/**
 * Ayarlar > Özet stili: kullanıcının kendi kuralları ve özet şablonu.
 * Kurallar her yeni özet isteminde modele veriliyor (services/ai.ts).
 */
export const SummaryStyleSection: React.FC<SummaryStyleSectionProps> = ({ onNotify }) => {
  const { t } = useLanguage();
  const { prefs, isLoaded, savePrefs } = usePreferences();

  const [rules, setRules] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [plan, setPlan] = useState<AccountPlan>('basic');

  // Kayıtlı kurallar yüklenince forma al.
  useEffect(() => {
    if (isLoaded) setRules(prefs.summaryStyle.rules);
  }, [isLoaded, prefs.summaryStyle.rules]);

  // Şablon kilitleri hesabın planına göre gösteriliyor.
  useEffect(() => {
    let alive = true;
    void Promise.resolve()
      .then(() => window.zabit.getPlanUsage())
      .then((usage) => {
        if (alive) setPlan(usage.plan);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const template = templateById(prefs.summaryStyle.templateId);
  const cleaned = rules.map((rule) => rule.trim()).filter(Boolean);
  const isDirty = JSON.stringify(cleaned) !== JSON.stringify(prefs.summaryStyle.rules);

  const updateRule = (index: number, value: string) => {
    setRules((current) => current.map((rule, position) => (position === index ? value : rule)));
  };

  const removeRule = (index: number) => {
    setRules((current) => current.filter((_, position) => position !== index));
  };

  const saveRules = async (next: string[]) => {
    setIsSaving(true);
    try {
      await savePrefs({ summaryStyle: { ...prefs.summaryStyle, rules: next } });
      onNotify('success', t('Özet kuralların kaydedildi.', 'Your summary rules have been saved.'));
    } catch (err) {
      onNotify('error', (err as Error).message || t('Kurallar kaydedilemedi.', 'Could not save the rules.'));
    } finally {
      setIsSaving(false);
    }
  };

  const chooseTemplate = async (templateId: string) => {
    try {
      await savePrefs({ summaryStyle: { ...prefs.summaryStyle, templateId } });
      onNotify('success', t('Şablon seçildi.', 'Template selected.'));
    } catch (err) {
      onNotify('error', (err as Error).message || t('Şablon kaydedilemedi.', 'Could not save the template.'));
    }
  };

  return (
    <div className="space-y-6">
      {/* — Kurallar — */}
      <Card>
        <CardHeader>
          <CardTitle>{t('Özet kuralların', 'Your summary rules')}</CardTitle>
          <CardDescription>
            {t(
              'Özetlerin nasıl yazılmasını istediğini yaz. Her kural, bundan sonraki her özete uygulanır — örneğin "önce amacı ve en önemli çıktıyı yaz" ya da "aksiyon maddeleri her zaman en üstte olsun".',
              'Tell TalkNote how you want your summaries written. Each rule is applied to every new summary — for example, lead with the purpose and key takeaway, or always put action items first.'
            )}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          {rules.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t(
                'Henüz kural yok. Aşağıdan bir kural ekle ya da önerilen toplantı biçimini kullan.',
                'No rules yet. Add one below, or use the recommended meeting format.'
              )}
            </p>
          ) : (
            <ul className="space-y-2">
              {rules.map((rule, index) => (
                <li key={index} className="flex items-center gap-2">
                  <Input
                    value={rule}
                    maxLength={MAX_RULE_LENGTH}
                    autoFocus={rule === '' && index === rules.length - 1}
                    placeholder={t('ör. Kararları madde madde yaz.', 'e.g. List decisions as separate bullets.')}
                    onChange={(event) => updateRule(index, event.target.value)}
                    aria-label={t(`${index + 1}. kural`, `Rule ${index + 1}`)}
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => removeRule(index)}
                    title={t('Kuralı sil', 'Delete rule')}
                    aria-label={t('Kuralı sil', 'Delete rule')}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          <Separator />

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={rules.length >= MAX_RULES}
              onClick={() => setRules((current) => [...current, ''])}
            >
              <Plus className="size-4" />
              {t('Kural ekle', 'Add a rule')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={isSaving}
              onClick={() => setRules(recommendedRules().slice(0, MAX_RULES))}
            >
              <Sparkles className="size-4" />
              {t('Önerilen toplantı biçimini kullan', 'Use the recommended meeting format')}
            </Button>
            <span className="ml-auto text-[0.8rem] text-muted-foreground">
              {t(`${cleaned.length}/${MAX_RULES} kural`, `${cleaned.length}/${MAX_RULES} rules`)}
            </span>
          </div>
        </CardContent>

        <div className="flex items-center gap-2 px-6">
          <Button disabled={!isDirty || isSaving} onClick={() => void saveRules(cleaned)}>
            {isSaving && <Loader2 className="size-4 animate-spin" />}
            {t('Kaydet', 'Save')}
          </Button>
          {isDirty && (
            <Button variant="ghost" disabled={isSaving} onClick={() => setRules(prefs.summaryStyle.rules)}>
              {t('Vazgeç', 'Cancel')}
            </Button>
          )}
        </div>
      </Card>

      {/* — Şablon — */}
      <Card>
        <CardHeader>
          <CardTitle>{t('Şablon', 'Template')}</CardTitle>
          <CardDescription>
            {t(
              'Şablon, özetin hangi başlıklarla yazılacağını belirler. Kurallarınla birlikte çalışır.',
              'The template decides which sections the summary is written with. It works together with your rules.'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-1.5">
              <Label>{template?.name ?? t('Şablon seçilmedi', 'No template selected')}</Label>
              <p className="text-[0.8rem] text-muted-foreground">
                {template?.description ??
                  t('Bir şablon seçerek başlayabilirsin.', 'Pick a template to get started.')}
              </p>
            </div>
            <Button variant="outline" className="shrink-0" onClick={() => setIsPickerOpen(true)}>
              <LayoutTemplate className="size-4" />
              {t('Şablon seç', 'Choose template')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <p className="text-[0.8rem] text-muted-foreground">
        {t(
          'Kurallar ve şablon bundan sonra üretilen özetlere uygulanır, eski notlara dokunmaz. Eski bir toplantıyı yeni kurallarla özetlemek için notun içindeki "Özeti yenile"yi kullanabilirsin.',
          'Rules and the template apply to summaries created from now on, not to past notes. To re-summarize an old meeting with the new rules, use "Regenerate summary" inside the note.'
        )}
      </p>

      <TemplatePickerDialog
        open={isPickerOpen}
        templateId={prefs.summaryStyle.templateId}
        plan={plan}
        onClose={() => setIsPickerOpen(false)}
        onSelect={(id) => void chooseTemplate(id)}
      />
    </div>
  );
};
